import { DeleteOrganisationCard } from '@/components/features/organisations/DeleteOrganisationCard'
import { Form } from '@/components/forms/Form'
import { TextController } from '@/components/forms/FormController'
import { useClient } from '@/context/ClientContext'
import { usePermissions } from '@/context/PermissionsContext'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { zodResolver } from '@/lib/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { Permission } from '@droidmap/permissions-service-contract'

export const Route = createFileRoute('/_authenticated/orgs/$orgId/settings')({
  component: Settings,
  loader: async ({ context }) => {
    return { crumb: 'Settings', metadata: context.metadata }
  },
})

const organisationSettingsSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'Organisation name is required' })
    .max(100, { error: 'Organisation name must be 100 characters or less' }),
})

type OrganisationSettingsForm = z.infer<typeof organisationSettingsSchema>

function Settings() {
  const { organisationClient } = useClient()
  const { orgId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { hasPermission } = usePermissionCheck()
  const { refreshPermissions } = usePermissions()
  const canDeleteOrganisation = hasPermission(
    Permission.DELETE_ORGANIZATION,
    orgId,
  )

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // React Hook Form for organisation settings
  const {
    control: orgControl,
    handleSubmit: handleOrgSubmit,
    formState: { errors: orgErrors, isDirty },
    reset: resetOrgForm,
  } = useForm<OrganisationSettingsForm>({
    resolver: zodResolver(organisationSettingsSchema),
    defaultValues: {
      name: '',
    },
  })

  const { data: organisation } = useQuery({
    queryKey: ['organisation', orgId],
    queryFn: () => organisationClient.getOrganisation({ orgId }, metadata),
  })

  const updateOrganisationMutation = useMutation({
    mutationFn: async (data: OrganisationSettingsForm) => {
      return await organisationClient.updateOrganisation(
        {
          orgId: orgId,
          name: data.name,
        },
        metadata,
      )
    },
    onSuccess: (updatedOrganisation) => {
      queryClient.setQueryData(['organisation', orgId], updatedOrganisation)
      resetOrgForm({ name: updatedOrganisation.name })
    },
  })

  const onSubmitOrgSettings = (data: OrganisationSettingsForm) => {
    updateOrganisationMutation.mutate(data)
  }

  const deleteOrganisationMutation = useMutation({
    mutationFn: async () => {
      return await organisationClient.deleteOrganisation({ orgId }, metadata)
    },
    onSuccess: async () => {
      setDeleteDialogOpen(false)
      // Back to the list, where the org now shows as Deleted (#2973: a real
      // org is soft-deleted and restorable for 30 days; a test org is purged
      // outright, and navigating first keeps the parent loader from
      // refetching it into notFound() on the way out).
      await navigate({ to: '/orgs' })
      // Drop the org's caches; invalidate the list so /orgs refreshes.
      queryClient.removeQueries({ queryKey: ['organisation', orgId] })
      queryClient.removeQueries({ queryKey: ['org-rollup', orgId] })
      queryClient.invalidateQueries({ queryKey: ['organisations'] })
      // Grants survive a soft delete but are gone after a purge — refetch.
      await refreshPermissions()
    },
  })

  // Sync form state when organisation data loads
  useEffect(() => {
    if (organisation?.name) {
      resetOrgForm({ name: organisation.name })
    }
  }, [organisation?.name, resetOrgForm])

  return (
    <div className="space-y-5">
      {/* General Settings */}
      <Form onSubmit={handleOrgSubmit(onSubmitOrgSettings)}>
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>
              Update your organisation&rsquo;s display name. Visible across
              every project and asset set.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TextController
              name="name"
              control={orgControl}
              label="Organisation Name"
              errors={orgErrors}
            />
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={!isDirty || updateOrganisationMutation.isPending}
            >
              {updateOrganisationMutation.isPending
                ? 'Updating...'
                : 'Update Organisation'}
            </Button>
          </CardFooter>
        </Card>
      </Form>

      {/* Danger Zone */}
      {organisation && (
        <DeleteOrganisationCard
          organisationName={organisation.name}
          canDelete={canDeleteOrganisation}
          isDialogOpen={deleteDialogOpen}
          isPending={deleteOrganisationMutation.isPending}
          error={
            deleteOrganisationMutation.isError
              ? deleteOrganisationMutation.error
              : null
          }
          onRequestDelete={() => setDeleteDialogOpen(true)}
          onCancelDelete={() => setDeleteDialogOpen(false)}
          onConfirmDelete={() => deleteOrganisationMutation.mutate()}
        />
      )}
    </div>
  )
}
